import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TripService } from '../../../../services/trip.service';
import { WaypointService } from '../../../../services/waypoint.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { UserProfileService } from '../../../../services/user-profile.service';
import { SocialService } from '../../../../services/social.service';
import { SnackbarComponent } from '@predigle/micro/component-ui';
import { ExitButtonComponent } from '../../../../shared/components/buttons/exit-button/exit-button.component';
import { TourAnchorDirective } from '../../../../features/learning-center/engine/tour-anchor.directive';
import {
  FLEET_RECIPIENT_OFFSET,
  TripRestrictedFleetRef,
  RestrictiveFleet,
  toggledRestrictiveRecipientSelection,
  isRestrictiveRecipientDisabled,
  hasRestrictiveFleetSelection,
  normalizeRestrictiveRecipientSelection,
} from '../../../../shared/utils/restrictive-sharing.utils';

interface FleetItem extends RestrictiveFleet {
  name: string;
  image_url?: string;
  membersCount: number;
}

interface DockItem {
  id: string;
  name: string;
  image_url?: string;
  vesselName?: string;
}

@Component({
  selector: 'app-throw-lines',
  standalone: true,
  imports: [CommonModule, FormsModule, SnackbarComponent, ExitButtonComponent, TourAnchorDirective],
  templateUrl: './throw-lines.component.html',
  styleUrl: './throw-lines.component.scss',
})
export class ThrowLinesComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() startTrip = new EventEmitter<string>();

  name = '';
  description = '';
  selectedPrivacy = 'private';
  vesselRef = '';

  fleets: FleetItem[] = [];
  dockmates: DockItem[] = [];
  selectedFollowers = new Set<number>();
  visibilitySearchQuery = '';
  loadingRecipients = false;

  restrictedFleetRef: TripRestrictedFleetRef | null = null;

  showSnackbar = false;
  snackbarMessage = '';
  snackbarType: 'success' | 'error' | 'warning' = 'success';

  readonly FLEET_OFFSET = FLEET_RECIPIENT_OFFSET;

  constructor(
    private tripService: TripService,
    private waypointService: WaypointService,
    private authService: AuthService,
    private userProfileService: UserProfileService,
    private socialService: SocialService,
  ) {}

  ngOnInit() {
    this.fetchDefaultSharing();
  }

  // ─── Key helpers ──────────────────────────────────────────────
  fleetKeyIndex(fleetArrayIndex: number): number {
    return FLEET_RECIPIENT_OFFSET + fleetArrayIndex;
  }

  dmKeyIndex(dmArrayIndex: number): number {
    return dmArrayIndex;
  }

  isChecked(keyIndex: number): boolean {
    return this.selectedFollowers.has(keyIndex);
  }

  isDisabled(keyIndex: number): boolean {
    return isRestrictiveRecipientDisabled(
      keyIndex,
      this.isChecked(keyIndex),
      this.selectedFollowers,
      this.fleets,
    );
  }

  get disableEveryoneOption(): boolean {
    return hasRestrictiveFleetSelection(this.selectedFollowers, this.fleets);
  }

  onToggle(keyIndex: number) {
    this.selectedFollowers = toggledRestrictiveRecipientSelection(
      this.selectedFollowers,
      keyIndex,
      this.fleets,
    );
  }

  // ─── Filtering ────────────────────────────────────────────────
  get filteredFleets(): FleetItem[] {
    const q = this.visibilitySearchQuery.trim().toLowerCase();
    if (!q) return this.fleets;
    return this.fleets.filter(f => f.name?.toLowerCase().includes(q));
  }

  get filteredDockmates(): DockItem[] {
    const q = this.visibilitySearchQuery.trim().toLowerCase();
    if (!q) return this.dockmates;
    return this.dockmates.filter(m => m.name?.toLowerCase().includes(q));
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0][0].toUpperCase();
  }

  // ─── Default sharing ──────────────────────────────────────────
  private fetchDefaultSharing() {
    const userId = this.authService.getUserIdFromToken();
    if (!userId) return;

    this.userProfileService.getProfileById(userId).subscribe({
      next: (res: any) => {
        if (res?.settings?.privacy) {
          const privacy = res.settings.privacy;
          if (privacy.default_visibility === 0) this.selectedPrivacy = 'private';
          else if (privacy.default_visibility === 1) this.selectedPrivacy = 'dockmates';
          else if (privacy.default_visibility === 2) this.selectedPrivacy = 'public';

          if (privacy.default_fleet_ids?.length > 0 || privacy.default_friend_ids?.length > 0) {
            this.fetchFleetsAndFriends(
              privacy.default_fleet_ids ?? [],
              privacy.default_friend_ids ?? [],
            );
          }
        }
      },
    });
  }

  selectPrivacy(option: string) {
    if (option === 'public' && this.disableEveryoneOption) return;
    this.selectedPrivacy = option;
    if (option === 'dockmates' && this.fleets.length === 0 && this.dockmates.length === 0) {
      this.fetchFleetsAndFriends();
    }
  }

  fetchFleetsAndFriends(defaultFleetIds: string[] = [], defaultFriendIds: string[] = []) {
    this.loadingRecipients = true;

    const tokenUserId = this.authService.getUserIdFromToken();
    const objUserId = this.authService.getUserObjectId();

    this.waypointService.getFleets().subscribe({
      next: (res) => {
        this.fleets = (res.results || []).map((f: any) => ({
          id: f.id || f._id || '',
          name: f.name || '',
          image_url: f.image_url || f.image || '',
          membersCount: f.members_count ?? f.membersCount ?? 0,
          isRestrictive: f.is_restrictive ?? false,
          myRole: f.my_role ?? f.myRole ?? '',
        }));

        // Pre-select defaults then normalize
        const preSelected = new Set<number>();
        this.fleets.forEach((fleet, i) => {
          if (defaultFleetIds.includes(fleet.id)) preSelected.add(this.fleetKeyIndex(i));
        });

        this.socialService.myDockmates(1, 100).subscribe({
          next: (dmRes) => {
            const results = (dmRes.results || []).filter((m: any) => {
              const ids = [m.user?._id, m.user?.user_id];
              return !ids.includes(tokenUserId) && !ids.includes(objUserId);
            });
            this.dockmates = results.map((m: any) => ({
              id: m.user?._id || '',
              name: m.user?.name || '',
              image_url: m.user?.image_url || '',
              vesselName: m.vessel_name || '',
            }));

            this.dockmates.forEach((dm, i) => {
              if (defaultFriendIds.includes(dm.id)) preSelected.add(this.dmKeyIndex(i));
            });

            this.selectedFollowers = normalizeRestrictiveRecipientSelection(preSelected, this.fleets);
            this.loadingRecipients = false;
          },
          error: () => { this.loadingRecipients = false; },
        });
      },
      error: () => { this.loadingRecipients = false; },
    });
  }

  // ─── Build share payload from Set ─────────────────────────────
  private buildAudiencePayload(): { friend_ids: string[]; fleet_ids: string[] } {
    const fleet_ids: string[] = [];
    const friend_ids: string[] = [];

    for (const keyIndex of this.selectedFollowers) {
      if (keyIndex >= FLEET_RECIPIENT_OFFSET) {
        const i = keyIndex - FLEET_RECIPIENT_OFFSET;
        if (i >= 0 && i < this.fleets.length && this.fleets[i].id) {
          fleet_ids.push(this.fleets[i].id);
        }
      } else {
        const i = keyIndex;
        if (i >= 0 && i < this.dockmates.length && this.dockmates[i].id) {
          friend_ids.push(this.dockmates[i].id);
        }
      }
    }
    return { friend_ids, fleet_ids };
  }

  // ─── Trip actions ─────────────────────────────────────────────
  onCancel() {
    this.close.emit();
  }

  onThrowLines() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => this.createTrip(position.coords.latitude, position.coords.longitude),
        () => {
          this.showError('Could not get your location. Defaulting to 0,0');
          this.createTrip(0, 0);
        },
      );
    } else {
      this.showError('Geolocation is not supported by this browser.');
    }
  }

  createTrip(lat: number, lng: number) {
    let apiPrivacy = 'public';
    if (this.selectedPrivacy === 'private') apiPrivacy = 'private';
    else if (this.selectedPrivacy === 'dockmates') apiPrivacy = 'custom';

    const payload = {
      name: this.name,
      description: this.description,
      vessel_ref: {},
      trip_type: 'cruise',
      status: 'planned',
      privacy: apiPrivacy,
      start_position: { latitude: lat, longitude: lng },
      start_location_name: 'Start Location',
      metrics: { distance_unit: '23', speed_unit: '7' },
      environment: { weather_condition: 'Clear' },
    };

    this.tripService.setStartCoords(lat, lng);

    this.tripService.startTrip(payload).subscribe({
      next: (res) => {
        const tripId = res?.id || res?._id || res?.trip_id;
        if (this.selectedPrivacy === 'dockmates' && tripId) {
          this.shareWithSelected(tripId);
        } else {
          this.showSuccess('Trip started successfully');
          setTimeout(() => this.startTrip.emit(tripId), 1000);
        }
      },
      error: () => this.showError('Failed to start trip'),
    });
  }

  shareWithSelected(tripId: string) {
    const { friend_ids, fleet_ids } = this.buildAudiencePayload();

    if (friend_ids.length === 0 && fleet_ids.length === 0) {
      this.showSuccess('Trip started successfully');
      setTimeout(() => this.startTrip.emit(tripId), 1000);
      return;
    }

    this.tripService.shareTrip(tripId, { friend_ids, fleet_ids, message: this.description || ' ' }).subscribe({
      next: (res) => {
        this.showSuccess(res.message || 'Trip shared successfully');
        setTimeout(() => this.startTrip.emit(tripId), 1000);
      },
      error: () => {
        this.showError('Trip started but sharing failed');
        setTimeout(() => this.startTrip.emit(tripId), 1000);
      },
    });
  }

  // ─── Snackbar ─────────────────────────────────────────────────
  private showSuccess(message: string) {
    this.snackbarMessage = message;
    this.snackbarType = 'success';
    this.showSnackbar = true;
  }

  private showError(message: string) {
    this.snackbarMessage = message;
    this.snackbarType = 'error';
    this.showSnackbar = true;
  }

  onSnackbarClose() { this.showSnackbar = false; }
  onSnackbarAutoClose() { this.showSnackbar = false; }
}
